/**
 * Display-only transformations for the plain-text preview pane.
 *
 * Unicode's line-breaking algorithm (UAX #14) treats hyphen (`-`), em dash
 * (`—`), and en dash (`–`) as soft break opportunities — meaning the browser
 * will wrap a line after one of these characters even when our preview pane
 * has explicit CSS to prevent it. This is the same behavior recipients see in
 * a narrow email-client pane, but in our editor it confuses operators who
 * think the rendered output has a real line break.
 *
 * The fix runs only at display time: characters in the rendered string passed
 * to the preview `<pre>` get replaced with non-breaking equivalents (or
 * wrapped with U+2060 Word Joiners, which are zero-width and invisible). The
 * bytes saved to the DB and sent through Bison are unchanged.
 */
export function applyPreviewDisplayFixes(text: string): string {
  return (
    text
      // Hyphen between letters → U+2011 (non-breaking hyphen). Lookaround
      // (vs. a capturing pattern) handles chained hyphens like `state-of-the-art`
      // — a capture-and-replace match consumes the trailing letter and skips
      // the next hyphen, leaving it still breakable.
      .replace(/(?<=[A-Za-z])-(?=[A-Za-z])/g, '‑')
      // Em dash and en dash → wrap with U+2060 (Word Joiner) on each side so
      // the browser won't break a line after them. Word Joiner is zero-width.
      .replace(/([—–])/g, '⁠$1⁠')
      // Lone hyphen between spaces (e.g. `Hi - thanks`) → also wrap with Word
      // Joiners. UAX #14 treats this as a break opportunity; in our narrow
      // preview pane that produces an unintended line break after the dash.
      .replace(/(?<=\s)-(?=\s)/g, '⁠-⁠')
  )
}
